import cx from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { Ratio } from 'components/@tedui';
import { Link } from 'components/router';
import { Heading, Text } from 'components/typography';

type ArticleCardProps = {
  className?: string;
  datePosted?: string;
  description?: string;
  image?: string;
  link: string;
  title: string;
};

const ArticleCard = ({
  className = '',
  datePosted,
  description,
  image,
  link,
  title,
  ...props
}: ArticleCardProps) => {
  const textClasses = cx({
    'mt-4 md:ml-6 md:mt-0 xl:ml-8': image,
    'md:flex md:flex-col md:justify-center': !description && image
  });

  const datePostedClasses = cx('mt-4 text-gray-500', {
    'md:mt-2 xl:mt-3': !description
  });

  const intl = useIntl();

  return (
    <Link href={link} className={`group block md:flex ${className}`} {...props}>
      {image && (
        <div className="md:w-56 md:shrink-0 xl:w-60" aria-hidden>
          <Ratio ratio={[16, 9]}>
            <div
              style={{ backgroundImage: `url(${image})` }}
              className="size-full bg-gray-300 bg-cover bg-center"
            />
          </Ratio>
        </div>
      )}
      <div className={textClasses}>
        <Heading
          size="m"
          className="transition-colors group-hover:text-gray-700 group-focus:text-gray-700"
        >
          <span
            // Title is coming from RSS and contains encoded HTML entities
            // therefore it should be safe to use dangerouslySetInnerHTML
            // and cheaper than importing something like https://www.npmjs.com/package/he

            dangerouslySetInnerHTML={{ __html: title }}
          />
        </Heading>
        {description && (
          <Text
            size="m"
            className="mt-4 text-gray-900 md:mt-2 xl:mt-3"
            {...{
              dangerouslySetInnerHTML: { ...{ __html: description } }
            }}
          />
        )}
        {datePosted && (
          <Text size="s" className={datePostedClasses}>
            <FormattedMessage
              defaultMessage="Posted {date}"
              values={{
                date: intl.formatDate(new Date(datePosted), {
                  year: 'numeric',
                  month: 'short'
                })
              }}
            />
          </Text>
        )}
      </div>
    </Link>
  );
};

export default ArticleCard;
